import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useEnqueueSnackbar } from 'hooks/useEnqueueSnackbar';
import type { UseModalResult } from 'hooks/useModal';
import { useDeleteGoToTest } from '../api';
import { ConfirmDialog } from 'components/common';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import {
  createCabinetMutationTracking,
  trackCabinetSubmitAttempt,
} from 'lib/datadog';

interface DeleteGoToTestCommonProps {
  id: number;
  name?: string;
}

interface DeleteGoToTestItemProps
  extends DeleteGoToTestCommonProps,
    Pick<UseModalResult, 'open' | 'close' | 'isOpen'> {
}

const DeleteGoToTest: FC<DeleteGoToTestItemProps> = ({ isOpen, close, id, name }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { onShowAlert } = useEnqueueSnackbar();
  const navigate = useNavigate();

  const deleteMutate = useDeleteGoToTest({
    config: createCabinetMutationTracking({
      platform: 'tiktok',
      entity: 'go_to_test',
      mode: 'delete',
      onSuccess: () => {
        onShowAlert(`"${name}" flow deleted`);
        close();
        navigate(`/cabinet/tiktok/go-to-test`, { replace: true });
      },
      onError: () => {
        setErrorMessage('Internal Error');
      },
    }),
  });

  const onConfirm = useCallback(() => {
    setErrorMessage(null);
    trackCabinetSubmitAttempt('tiktok', 'go_to_test', 'delete', { id });
    deleteMutate.mutate({ id });
  }, [deleteMutate, id]);

  return (
    <>
      <ConfirmDialog
        title={`Delete GoToTest ${name ? name : ''}?`}
        text={
          <>
            <Typography variant="body1">
              {name ?? 'GoToTest'} will be deleted without the possibility of recovery
            </Typography>
            {!!errorMessage && (
              <Typography
                variant="body1"
                sx={(t) => ({ color: t.palette.error.main, pt: 2 })}
              >
                {errorMessage}
              </Typography>
            )}
          </>
        }
        confirmButton="Delete"
        cancelButton="Cancel"
        isOpen={isOpen}
        isPending={deleteMutate.isPending}
        close={() => {
          !deleteMutate.isPending ? close() : null;
        }}
        onCancel={() => {
          !deleteMutate.isPending ? close() : null;
        }}
        onConfirm={onConfirm}
      />
    </>
  );
};

export default DeleteGoToTest;
